/**
 * Dataverse (Dynamics CRM) API client
 * Provides access to solutions, solution-aware flows, approvals, and AI Builder
 */

import { requestWithRetry } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import { createChildLogger } from "../utils/logger.js";
import { ApiError, ConfigurationError, RateLimitError, wrapApiCall } from "../utils/errors.js";
import {
  GUID_PATTERN,
  validateODataFilter,
  validateODataFieldList,
  validateLogicalName,
  validateEntitySetName,
  validateRowId,
  validateDataverseUrl,
  sanitizeErrorMessage, validateODataExpand } from "../utils/security.js";

const logger = createChildLogger({ module: "dataverse-api" });

/** Fixed messages only, so MCP can preserve write outcomes without generic retry advice. */
export class CopilotComponentUpdateError extends ApiError {
  constructor(reason: "authentication" | "transport" | "http" | "preflight", statusCode = 502) {
    const status = (reason === "http" || reason === "preflight") && Number.isInteger(statusCode) && statusCode >= 100 && statusCode <= 599
      ? statusCode : 502;
    const context = "Update Copilot component conditionally";
    super(
      reason === "preflight"
        ? `Guarded Copilot component preflight read failed (HTTP ${status}); no update or create request sent, no automatic retry`
        : reason === "authentication"
        ? `${context}: authentication failed; no update request sent, no automatic retry`
        : reason === "transport"
          ? `${context}: transport failed; outcome unknown, no automatic retry. Inspect the component before considering another write.`
          : status === 412
            ? `${context}: the component changed after preflight (HTTP 412); no automatic retry`
            : `${context}: HTTP ${status}; update not verified, no automatic retry`,
      status,
    );
  }
}

/** Fixed diagnostics for the physical binding row; never echo connection or response data. */
export class CopilotConnectionReferenceUpdateError extends ApiError {
  constructor(
    reason: "invalid_input" | "authentication" | "transport" | "http" | "preflight",
    statusCode = 502,
  ) {
    const status = reason === "invalid_input" ? 400 :
      (reason === "http" || reason === "preflight") && Number.isInteger(statusCode) &&
      statusCode >= 100 && statusCode <= 599 ? statusCode : 502;
    const context = "Update Copilot connection reference conditionally";
    super(
      reason === "invalid_input"
        ? `${context}: invalid input; no update request sent, no automatic retry`
        : reason === "preflight"
          ? `${context}: preflight read failed (HTTP ${status}); no update or create request sent, no automatic retry`
          : reason === "authentication"
            ? `${context}: authentication failed; no update request sent, no automatic retry`
            : reason === "transport"
              ? `${context}: transport failed; outcome unknown, no automatic retry. Inspect the connection reference before considering another write.`
              : status === 412
                ? `${context}: the connection reference changed after preflight (HTTP 412); no automatic retry`
                : `${context}: HTTP ${status}; update not verified, no automatic retry`,
      status,
    );
    this.name = "CopilotConnectionReferenceUpdateError";
  }
}

export interface DataverseApiConfig {
  authProvider: AuthProvider;
  environmentUrl: string; // e.g., "org.crm.dynamics.com"
  /** Injectable delay used by asynchronous-operation polling tests. */
  sleep?: (ms: number) => Promise<void>;
}

export interface DataverseOperationOptions {
  /** Safe label used in logs and errors. */
  context?: string;
  /** Response cap. The hard upper bound is 128 MiB. */
  maxResponseBytes?: number;
}

export interface DataverseAsyncOperation {
  asyncoperationid: string;
  name?: string;
  statecode: number;
  statuscode: number;
  message?: string;
  friendlymessage?: string;
  errorcode?: number;
}

export interface DataverseAsyncPollResult {
  completed: boolean;
  succeeded: boolean;
  operation: DataverseAsyncOperation;
}

export interface Solution {
  solutionid: string;
  uniquename: string;
  friendlyname: string;
  version: string;
  ismanaged: boolean;
  installedon: string;
  publisherid: {
    friendlyname: string;
  };
}

export interface SolutionFlow {
  workflowid: string;
  name: string;
  category: number; // 5 = Modern Flow
  statecode: number; // 0 = Draft, 1 = Activated
  statuscode: number;
  createdon: string;
  modifiedon: string;
  ownerid: {
    name: string;
  };
  solutionid?: string;
  clientdata?: string; // JSON definition
}

export interface CanvasApp {
  canvasappid: string;
  name: string;
  displayname: string;
  description?: string;
  appversion: string;
  status: string;
  /** `canvasapp` uses createdtime/lastmodifiedtime, NOT createdon/modifiedon. */
  createdtime?: string;
  lastmodifiedtime?: string;
  owner?: string;
  commitmessage?: string;
}

export interface AIModel {
  msdyn_aimodelid: string;
  msdyn_name: string;
  msdyn_modelcreationcontext: string;
  statecode: number;
  statuscode: number;
  createdon: string;
  modifiedon: string;
}

/** `msdyn_flow_approval_stage` picklist value meaning the approval is finished. */
const APPROVAL_STAGE_COMPLETE = 192351000;

export interface ApprovalRequest {
  msdyn_flow_approvalid: string;
  /** The approval's name. There is no `..._title` column on this table. */
  msdyn_flow_approval_name?: string;
  /** Picklist: 192350000 Not Specified, 192350001 Basic, 192351000 Complete. */
  msdyn_flow_approval_stage?: number;
  msdyn_flow_approval_stage_label?: string;
  /** There is no `..._requestdate` column; the request time is `createdon`. */
  createdon?: string;
  msdyn_flow_approval_dueon?: string;
  msdyn_flow_approval_result?: string;
  ownerid?: {
    name: string;
  };
}

export interface DataverseTable {
  LogicalName: string;
  DisplayName: { UserLocalizedLabel?: { Label: string } };
  EntitySetName: string;
  Description?: { UserLocalizedLabel?: { Label: string } };
  IsCustomEntity: boolean;
  IsManaged: boolean;
  SchemaName: string;
  PrimaryIdAttribute: string;
  PrimaryNameAttribute?: string;
}

export interface DataverseAttribute {
  LogicalName: string;
  DisplayName: { UserLocalizedLabel?: { Label: string } };
  AttributeType: string;
  IsCustomAttribute: boolean;
  RequiredLevel: { Value: string };
  Description?: { UserLocalizedLabel?: { Label: string } };
  SchemaName: string;
}

export type DataverseRow = Record<string, unknown>;

const DEFAULT_MAX_RESPONSE_SIZE = 50 * 1024 * 1024;
const MAX_ALLOWED_RESPONSE_SIZE = 128 * 1024 * 1024;
const DEFAULT_SLEEP = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Operation segments become part of the request path, so accept identifiers and
 * namespace-qualified identifiers only (for example, `WhoAmI` or
 * `Microsoft.Dynamics.CRM.AddAppComponents`).
 */
function validateOperationName(name: string): string {
  if (
    typeof name !== "string" ||
    name.length > 256 ||
    !/^[A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)*$/.test(name)
  ) {
    throw new ConfigurationError(
      "Invalid Dataverse operation name: expected an identifier or namespace-qualified identifier",
    );
  }
  return name;
}

function validateResponseLimit(value: number | undefined): number {
  const limit = value ?? DEFAULT_MAX_RESPONSE_SIZE;
  if (!Number.isInteger(limit) || limit < 1 || limit > MAX_ALLOWED_RESPONSE_SIZE) {
    throw new ConfigurationError(
      "Invalid Dataverse response limit: expected 1 byte through 128 MiB",
    );
  }
  return limit;
}

function serializeFunctionValue(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return `'${value.replace(/'/g, "''")}'`;
  if (typeof value === "boolean") return String(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new ConfigurationError("Dataverse function parameters must be finite numbers");
    }
    return String(value);
  }
  if (typeof value === "object") {
    try {
      const serialized = JSON.stringify(value);
      if (serialized !== undefined) return serialized;
    } catch {
      // Fall through to the consistent configuration error below.
    }
  }
  throw new ConfigurationError("Unsupported Dataverse function parameter: expected a JSON value");
}

function buildFunctionInvocation(
  operationName: string,
  parameters: Record<string, unknown>,
): { segment: string; query?: string } {
  const safeOperation = validateOperationName(operationName);
  const entries = Object.entries(parameters);
  if (entries.length === 0) return { segment: `${safeOperation}()` };

  const argumentsList: string[] = [];
  const aliases: string[] = [];
  entries.forEach(([name, value], index) => {
    const safeName = validateLogicalName(name);
    const alias = `@p${index}`;
    argumentsList.push(`${safeName}=${alias}`);
    aliases.push(`${alias}=${encodeURIComponent(serializeFunctionValue(value))}`);
  });
  return {
    segment: `${safeOperation}(${argumentsList.join(",")})`,
    query: aliases.join("&"),
  };
}

/**
 * True when an error (or its cause chain) is a DNS resolution failure —
 * the hostname simply doesn't exist.
 */
export function isHostNotFoundError(err: unknown): boolean {
  let current: unknown = err;
  for (let depth = 0; depth < 5 && current; depth++) {
    if (typeof current === "object") {
      const { code, message, cause } = current as {
        code?: unknown;
        message?: unknown;
        cause?: unknown;
      };
      if (code === "ENOTFOUND" || code === "EAI_AGAIN") return true;
      if (typeof message === "string" && message.includes("getaddrinfo ENOTFOUND")) return true;
      current = cause;
    } else {
      break;
    }
  }
  return false;
}

/**
 * Client for Dataverse Web API
 */
export class DataverseApi {
  private readonly authProvider: AuthProvider;
  private readonly sleep: (ms: number) => Promise<void>;
  private environmentUrl: string;
  private readonly apiVersion = "v9.2";

  constructor(config: DataverseApiConfig) {
    this.authProvider = config.authProvider;
    this.sleep = config.sleep ?? DEFAULT_SLEEP;
    this.environmentUrl = validateDataverseUrl(config.environmentUrl);
  }

  /**
   * Set the Dataverse environment URL dynamically
   */
  setEnvironmentUrl(url: string): void {
    this.environmentUrl = validateDataverseUrl(url);
  }

  /**
   * Get authorization header with bearer token for Dataverse
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    // Get token for Dataverse resource
    const token = await this.authProvider.getTokenForResource(`https://${this.environmentUrl}`);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      Accept: "application/json",
      Prefer: 'odata.include-annotations="*"',
    };
  }

  /**
   * Build Dataverse API URL
   */
  private buildUrl(entity: string, query?: string): string {
    const base = `https://${this.environmentUrl}/api/data/${this.apiVersion}/${entity}`;
    return query ? `${base}?${query}` : base;
  }

  /**
   * requestWithRetry with a friendlier failure when the Dataverse hostname
   * doesn't resolve. The common cause is an environment with no Dataverse
   * database — the hostname is auto-derived from the environment GUID and
   * simply doesn't exist — which otherwise surfaces as a cryptic
   * "getaddrinfo ENOTFOUND" error.
   */
  private async request(
    url: string,
    options: Parameters<typeof requestWithRetry>[1],
    retry?: Parameters<typeof requestWithRetry>[2],
  ): Promise<Awaited<ReturnType<typeof requestWithRetry>>> {
    try {
      return await (retry === undefined
        ? requestWithRetry(url, options)
        : requestWithRetry(url, options, retry));
    } catch (err) {
      if (isHostNotFoundError(err)) {
        throw new ApiError(
          `Dataverse host '${this.environmentUrl}' could not be resolved. ` +
            `This environment likely has no Dataverse database (required for solutions, Dataverse table/row, and Power Pages tools). ` +
            `If it does have one, the auto-derived hostname is wrong — set 'dataverseUrl' in your environment config to the org URL ` +
            `shown at make.powerautomate.com > Settings > Session details.`,
          404,
          { host: this.environmentUrl },
        );
      }
      throw err;
    }
  }

  /**
   * Handle API response errors
   */
  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string,
    maxResponseBytes = DEFAULT_MAX_RESPONSE_SIZE,
  ): Promise<T> {
    const { statusCode, body } = response;

    if (statusCode === 429) {
      await body.text();
      const retryAfter = response.headers["retry-after"];
      const retryMs = retryAfter ? parseInt(String(retryAfter), 10) * 1000 : undefined;
      throw new RateLimitError(retryMs);
    }

    // Stream-read body with size limit to prevent OOM on large responses
    const chunks: Buffer[] = [];
    let totalSize = 0;
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      totalSize += chunk.length;
      if (totalSize > maxResponseBytes) {
        body.destroy();
        throw new ApiError(
          `${context}: response too large (max ${Math.ceil(maxResponseBytes / 1024 / 1024)}MB)`,
          413,
        );
      }
      chunks.push(Buffer.from(chunk));
    }
    const responseText = Buffer.concat(chunks).toString("utf-8");

    if (statusCode >= 400) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = responseText;
      }
      // Sanitize before surfacing — Dataverse error bodies can carry record GUIDs,
      // user emails, and instance URLs. Pass the sanitized string as the thrown
      // details so it stays redacted even on paths that read error.details directly
      // (e.g. formatErrorMessage, which bypasses PowerAutomateError.toJSON).
      const safeDetails = sanitizeErrorMessage(
        typeof parsed === "string" ? parsed : JSON.stringify(parsed),
      );
      logger.error({ statusCode, errorDetails: safeDetails, context }, "Dataverse API error");
      throw new ApiError(`${context}: API error`, statusCode, safeDetails);
    }

    if (!responseText) {
      return { value: [] } as unknown as T;
    }

    return JSON.parse(responseText) as T;
  }

  // ==========================================================================
  // Actions, functions, and relationships
  // ==========================================================================

  /** Execute an unbound Dataverse Web API action. */
  async executeUnboundAction<T = Record<string, unknown>>(
    actionName: string,
    parameters: Record<string, unknown> = {},
    options: DataverseOperationOptions = {},
  ): Promise<T> {
    const safeAction = validateOperationName(actionName);
    const context = options.context ?? `Execute ${safeAction}`;
    const responseLimit = validateResponseLimit(options.maxResponseBytes);
    const url = this.buildUrl(safeAction);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "POST",
        headers,
        body: JSON.stringify(parameters),
      });
      return this.handleResponse<T>(response, context, responseLimit);
    }, context);
  }

  /** Execute an action bound to a Dataverse row. */
  async executeBoundAction<T = Record<string, unknown>>(
    entitySetName: string,
    id: string,
    actionName: string,
    parameters: Record<string, unknown> = {},
    options: DataverseOperationOptions = {},
  ): Promise<T> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    const safeId = validateRowId(id);
    const safeAction = validateOperationName(actionName);
    const context = options.context ?? `Execute ${safeAction}`;
    const responseLimit = validateResponseLimit(options.maxResponseBytes);
    const url = this.buildUrl(`${safeEntitySet}(${safeId})/${safeAction}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "POST",
        headers,
        body: JSON.stringify(parameters),
      });
      return this.handleResponse<T>(response, context, responseLimit);
    }, context);
  }

  /** Execute an unbound Dataverse Web API function using OData parameter aliases. */
  async executeUnboundFunction<T = Record<string, unknown>>(
    functionName: string,
    parameters: Record<string, unknown> = {},
    options: DataverseOperationOptions = {},
  ): Promise<T> {
    const invocation = buildFunctionInvocation(functionName, parameters);
    const context = options.context ?? `Execute ${validateOperationName(functionName)}`;
    const responseLimit = validateResponseLimit(options.maxResponseBytes);
    const url = this.buildUrl(invocation.segment, invocation.query);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<T>(response, context, responseLimit);
    }, context);
  }

  /** Read record sharing for a fixed Copilot row target; never follows continuation URLs. */
  async retrieveCopilotSharedPrincipalsAndAccess(
    entitySet: "bots" | "botcomponents",
    id: string,
  ): Promise<unknown> {
    if (entitySet !== "bots" && entitySet !== "botcomponents") {
      throw new ConfigurationError("Sharing reads require a bot or botcomponent target");
    }
    const safeId = validateRowId(id);
    const target = encodeURIComponent(JSON.stringify({ "@odata.id": `${entitySet}(${safeId})` }));
    return this.readCopilotSharingJson(
      "RetrieveSharedPrincipalsAndAccess(Target=@target)",
      `@target=${target}`,
    );
  }

  /** Bounded identity/owner preflight for the same two fixed sharing target types. */
  async getCopilotSharingTarget(
    entitySet: "bots" | "botcomponents",
    id: string,
  ): Promise<DataverseRow> {
    if (entitySet !== "bots" && entitySet !== "botcomponents") {
      throw new ConfigurationError("Sharing reads require a bot or botcomponent target");
    }
    const safeId = validateRowId(id);
    const select =
      entitySet === "bots"
        ? "botid,_ownerid_value"
        : "botcomponentid,_parentbotid_value,_ownerid_value";
    return (await this.readCopilotSharingJson(
      `${entitySet}(${safeId})`,
      `$select=${select}`,
    )) as DataverseRow;
  }

  /** Internal fixed-target caller only; never accepts a caller-supplied URL. */
  private async readCopilotSharingJson(entity: string, query: string): Promise<unknown> {
    const context = "Read Copilot Dataverse record sharing";
    const url = this.buildUrl(entity, query);
    const headers = await this.getAuthHeaders();
    delete headers.Prefer;
    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers }, { maxAttempts: 1 })
        .catch(() => {
          throw new ApiError(`${context}: request failed; no sharing result returned`, 502);
        });
      // The shared retry/response handlers drain 429 bodies without a byte cap.
      // This metadata-only read does not need those bodies or error payloads.
      if (response.statusCode === 429) {
        response.body.destroy();
        throw new RateLimitError();
      }
      if (response.statusCode !== 200) {
        response.body.destroy();
        throw new ApiError(
          `${context}: HTTP ${response.statusCode}; no sharing result returned`,
          response.statusCode,
        );
      }
      try {
        return await this.handleResponse<unknown>(response, context, 1024 * 1024);
      } catch (error) {
        if (error instanceof ApiError) throw error;
        throw new ApiError(`${context}: response could not be read as bounded JSON`, 502);
      }
    }, context);
  }

  /** Organization-wide index metadata, not agent knowledge ingestion or runtime. */
  async getSearchIndexStatus(): Promise<unknown> {
    return this.readSearchIndexMetadata("searchstatus");
  }

  /** Organization-wide document/storage aggregates; never returns indexed records. */
  async getSearchIndexStatistics(): Promise<unknown> {
    return this.readSearchIndexMetadata("searchstatistics");
  }

  /** Link one preflight-verified unowned evaluation case, only if its row is unchanged. */
  async linkCopilotEvaluationCaseIfUnchanged(
    caseId: string,
    setId: string,
    expectedEtag: string,
  ): Promise<void> {
    const safeCaseId = validateRowId(caseId);
    const safeSetId = validateRowId(setId);
    // Dataverse currently returns numeric weak entity tags. Unknown shapes are
    // refused, never weakened to wildcard matching or an unconditional PATCH.
    if (typeof expectedEtag !== "string" || !/^W\/"[0-9]{1,20}"$/.test(expectedEtag)) {
      throw new ConfigurationError("A verified Dataverse row ETag is required to link an evaluation case");
    }
    const context = "Link Copilot evaluation case conditionally";
    const headers = await this.getAuthHeaders().catch(() => {
      throw new ApiError(`${context}: authentication failed; no link request sent`, 502);
    });
    headers["If-Match"] = expectedEtag;
    headers.Prefer = "return=minimal";
    const response = await this.request(
      this.buildUrl(`botcomponents(${safeCaseId})`),
      {
        method: "PATCH",
        headers,
        body: JSON.stringify({ "ParentBotComponentId@odata.bind": `/botcomponents(${safeSetId})` }),
        signal: AbortSignal.timeout(30_000),
        headersTimeout: 30_000,
        bodyTimeout: 30_000,
      },
      { maxAttempts: 1 },
    ).catch(() => {
      throw new ApiError(`${context}: transport failed; outcome unknown, inspect the case before retrying`, 502);
    });
    response.body.destroy();
    if (response.statusCode === 200 || response.statusCode === 204) return;
    if (response.statusCode === 429) throw new RateLimitError();
    throw new ApiError(
      response.statusCode === 412
        ? `${context}: the case changed after preflight (HTTP 412); no automatic retry`
        : `${context}: HTTP ${response.statusCode}; linking is not verified, no automatic retry`,
      response.statusCode,
    );
  }

  /** Change only the physical connection ID on one unchanged Dataverse reference row. */
  async updateCopilotConnectionReferenceIfUnchanged(
    referenceId: string,
    data: { connectionid: string },
    expectedEtag: string,
  ): Promise<void> {
    if (
      typeof referenceId !== "string" || !GUID_PATTERN.test(referenceId) ||
      typeof expectedEtag !== "string" || !/^W\/"[0-9]{1,20}"$/.test(expectedEtag)
    ) throw new CopilotConnectionReferenceUpdateError("invalid_input");
    const plain = data !== null && typeof data === "object" && !Array.isArray(data) &&
      (Object.getPrototypeOf(data) === Object.prototype || Object.getPrototypeOf(data) === null);
    const keys = plain ? Reflect.ownKeys(data) : [];
    const property = plain ? Object.getOwnPropertyDescriptor(data, "connectionid") : undefined;
    if (
      keys.length !== 1 || keys[0] !== "connectionid" || !property ||
      !Object.hasOwn(property, "value") || typeof property.value !== "string" ||
      !/^[A-Za-z0-9_-]{1,300}$/.test(property.value)
    ) throw new CopilotConnectionReferenceUpdateError("invalid_input");
    const payload = { connectionid: property.value };
    const headers = await this.getAuthHeaders().catch(() => {
      throw new CopilotConnectionReferenceUpdateError("authentication");
    });
    headers["If-Match"] = expectedEtag;
    headers.Prefer = "return=minimal";
    const response = await this.request(
      this.buildUrl(`connectionreferences(${referenceId})`),
      {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(30_000),
        headersTimeout: 30_000,
        bodyTimeout: 30_000,
      },
      { maxAttempts: 1 },
    ).catch(() => {
      throw new CopilotConnectionReferenceUpdateError("transport");
    });
    try {
      response.body.destroy();
    } catch {
      throw new CopilotConnectionReferenceUpdateError("transport");
    }
    if (response.statusCode === 200 || response.statusCode === 204) return;
    throw new CopilotConnectionReferenceUpdateError("http", response.statusCode);
  }

  /** Update only reviewed authoring fields on one unchanged Copilot component. */
  async updateCopilotComponentIfUnchanged(
    componentId: string,
    data: { data: string; name?: string; description?: string },
    expectedEtag: string,
  ): Promise<void> {
    if (typeof componentId !== "string") {
      throw new ConfigurationError("Guarded component updates require a string component ID");
    }
    const safeId = validateRowId(componentId);
    if (typeof expectedEtag !== "string" || !/^W\/"[0-9]{1,20}"$/.test(expectedEtag)) {
      throw new ConfigurationError("A verified Dataverse row ETag is required for guarded component updates");
    }
    const keys = data !== null && typeof data === "object" && !Array.isArray(data) &&
      (Object.getPrototypeOf(data) === Object.prototype || Object.getPrototypeOf(data) === null)
      ? Object.keys(data) : [];
    if (
      !keys.includes("data") || keys.some((key) => !["data", "name", "description"].includes(key)) ||
      typeof data.data !== "string" || data.data.length === 0 || data.data.length > 500_000 ||
      (keys.includes("name") && (typeof data.name !== "string" || data.name.length === 0 || data.name.length > 200)) ||
      (keys.includes("description") && (typeof data.description !== "string" || data.description.length > 2000))
    ) {
      throw new ConfigurationError("Guarded component updates accept only bounded source, name and description fields");
    }
    // Rebuild rather than serialize arbitrary caller objects or inherited fields.
    const payload: { data: string; name?: string; description?: string } = { data: data.data };
    if (keys.includes("name")) payload.name = data.name;
    if (keys.includes("description")) payload.description = data.description;
    const headers = await this.getAuthHeaders().catch(() => {
      throw new CopilotComponentUpdateError("authentication");
    });
    headers["If-Match"] = expectedEtag;
    headers.Prefer = "return=minimal";
    const response = await this.request(
      this.buildUrl(`botcomponents(${safeId})`),
      {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(30_000),
        headersTimeout: 30_000,
        bodyTimeout: 30_000,
      },
      { maxAttempts: 1 },
    ).catch(() => {
      throw new CopilotComponentUpdateError("transport");
    });
    response.body.destroy();
    if (response.statusCode === 200 || response.statusCode === 204) return;
    throw new CopilotComponentUpdateError("http", response.statusCode);
  }

  /** Fixed documented GETs. No alternate route, retries, error-body draining or writes. */
  private async readSearchIndexMetadata(
    operation: "searchstatus" | "searchstatistics",
  ): Promise<unknown> {
    const context = "Read Dataverse search index metadata";
    const url = this.buildUrl(operation);
    let headers: Record<string, string>;
    try {
      headers = await this.getAuthHeaders();
    } catch {
      throw new ApiError(`${context}: authentication could not be obtained; no result returned`, 502);
    }
    delete headers.Prefer;
    headers["If-None-Match"] = "null";
    const response = await this.request(
      url,
      {
        method: "GET",
        headers,
        signal: AbortSignal.timeout(30_000),
        headersTimeout: 30_000,
        bodyTimeout: 30_000,
      },
      { maxAttempts: 1 },
    ).catch(() => {
      throw new ApiError(`${context}: transport failed; no result returned`, 502);
    });
    if (response.statusCode !== 200) {
      response.body.destroy();
      if (response.statusCode === 429) throw new RateLimitError();
      throw new ApiError(`${context}: HTTP ${response.statusCode}; no result returned`, response.statusCode);
    }
    try {
      return await this.handleResponse<unknown>(response, context, 1024 * 1024);
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 413) throw error;
      throw new ApiError(`${context}: invalid or incomplete bounded JSON; no result returned`, 502);
    }
  }

  /** Execute a function bound to a Dataverse row using OData parameter aliases. */
  async executeBoundFunction<T = Record<string, unknown>>(
    entitySetName: string,
    id: string,
    functionName: string,
    parameters: Record<string, unknown> = {},
    options: DataverseOperationOptions = {},
  ): Promise<T> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    const safeId = validateRowId(id);
    const invocation = buildFunctionInvocation(functionName, parameters);
    const context = options.context ?? `Execute ${validateOperationName(functionName)}`;
    const responseLimit = validateResponseLimit(options.maxResponseBytes);
    const url = this.buildUrl(
      `${safeEntitySet}(${safeId})/${invocation.segment}`,
      invocation.query,
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<T>(response, context, responseLimit);
    }, context);
  }

  /** Associate a row through a single- or collection-valued navigation property. */
  async associateReference(
    sourceEntitySetName: string,
    sourceId: string,
    relationshipName: string,
    targetEntitySetName: string,
    targetId: string,
  ): Promise<void> {
    const safeSourceSet = validateEntitySetName(sourceEntitySetName);
    const safeSourceId = validateRowId(sourceId);
    const safeRelationship = validateLogicalName(relationshipName);
    const safeTargetSet = validateEntitySetName(targetEntitySetName);
    const safeTargetId = validateRowId(targetId);
    const url = this.buildUrl(`${safeSourceSet}(${safeSourceId})/${safeRelationship}/$ref`);
    const headers = await this.getAuthHeaders();
    const targetUrl = `https://${this.environmentUrl}/api/data/${this.apiVersion}/${safeTargetSet}(${safeTargetId})`;

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ "@odata.id": targetUrl }),
      });
      await this.handleResponse<unknown>(response, `Associate ${safeRelationship}`);
    }, `Associate ${safeRelationship}`);
  }

  /**
   * Remove a relationship reference. Pass targetId for collection-valued
   * navigation properties; omit it for a single-valued navigation property.
   */
  async disassociateReference(
    sourceEntitySetName: string,
    sourceId: string,
    relationshipName: string,
    targetId?: string,
  ): Promise<void> {
    const safeSourceSet = validateEntitySetName(sourceEntitySetName);
    const safeSourceId = validateRowId(sourceId);
    const safeRelationship = validateLogicalName(relationshipName);
    const relatedSegment = targetId
      ? `${safeRelationship}(${validateRowId(targetId)})`
      : safeRelationship;
    const url = this.buildUrl(`${safeSourceSet}(${safeSourceId})/${relatedSegment}/$ref`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Disassociate ${safeRelationship}`);
    }, `Disassociate ${safeRelationship}`);
  }

  /** Get the current state of a Dataverse system job. */
  async getAsyncOperation(asyncOperationId: string): Promise<DataverseAsyncOperation> {
    const row = await this.getRow(
      "asyncoperations",
      asyncOperationId,
      // NOTE: no `progress` column. Dataverse's asyncoperation has statecode,
      // statuscode, message, friendlymessage and errorcode — asking for
      // `progress` fails the whole query with "Could not find a property named
      // 'progress'", which broke every async solution operation (and export
      // defaults to async, so export_solution failed by default).
      "asyncoperationid,name,statecode,statuscode,message,friendlymessage,errorcode",
    );
    return row as unknown as DataverseAsyncOperation;
  }

  /** Poll a Dataverse system job for at most 10 minutes. */
  async waitForAsyncOperation(
    asyncOperationId: string,
    options: { maxWaitMs?: number; pollIntervalMs?: number } = {},
  ): Promise<DataverseAsyncPollResult> {
    const safeId = validateRowId(asyncOperationId);
    const maxWaitMs = options.maxWaitMs ?? 60_000;
    const pollIntervalMs = options.pollIntervalMs ?? 2_000;
    if (!Number.isInteger(maxWaitMs) || maxWaitMs < 0 || maxWaitMs > 600_000) {
      throw new ConfigurationError("maxWaitMs must be an integer from 0 through 600000");
    }
    if (!Number.isInteger(pollIntervalMs) || pollIntervalMs < 100 || pollIntervalMs > 10_000) {
      throw new ConfigurationError("pollIntervalMs must be an integer from 100 through 10000");
    }

    const deadline = Date.now() + maxWaitMs;
    for (;;) {
      const operation = await this.getAsyncOperation(safeId);
      if (operation.statecode === 3 || [30, 31, 32].includes(operation.statuscode)) {
        return {
          completed: true,
          succeeded: operation.statuscode === 30,
          operation,
        };
      }
      if (Date.now() >= deadline) {
        return { completed: false, succeeded: false, operation };
      }
      await this.sleep(Math.min(pollIntervalMs, Math.max(0, deadline - Date.now())));
    }
  }

  // ==========================================================================
  // Solutions
  // ==========================================================================

  /**
   * List all solutions in the environment
   */
  async listSolutions(includeManaged = false): Promise<Solution[]> {
    logger.debug({ includeManaged }, "Listing solutions");

    let filter = "";
    if (!includeManaged) {
      filter = "$filter=ismanaged eq false";
    }

    const select = "$select=solutionid,uniquename,friendlyname,version,ismanaged,installedon";
    const expand = "$expand=publisherid($select=friendlyname)";
    const orderby = "$orderby=friendlyname asc";
    const query = [select, expand, orderby, filter].filter(Boolean).join("&");

    const url = this.buildUrl("solutions", query);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value: Solution[] }>(response, "List solutions");
      logger.info({ count: data.value.length }, "Listed solutions");
      return data.value;
    }, "List solutions");
  }

  /**
   * Get a solution by ID or unique name
   */
  async getSolution(solutionId: string): Promise<Solution> {
    const safeSolutionId = validateRowId(solutionId);
    logger.debug({ solutionId: safeSolutionId }, "Getting solution");

    const url = this.buildUrl(`solutions(${safeSolutionId})`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<Solution>(response, `Get solution ${solutionId}`);
    }, `Get solution ${solutionId}`);
  }

  // ==========================================================================
  // Solution Flows (Workflows)
  // ==========================================================================

  /**
   * List flows (modern flows have category = 5)
   */
  async listSolutionFlows(solutionId?: string): Promise<SolutionFlow[]> {
    logger.debug({ solutionId }, "Listing solution flows");

    // `ownerid` is a POLYMORPHIC lookup to `principal` (a systemuser or a
    // team), and the abstract base type has no `name` property — expanding it
    // as `ownerid($select=name)` fails the whole request with
    // "Could not find a property named 'name' on type
    // Microsoft.Dynamics.CRM.principal", which made this method unusable.
    // The owner's display name comes back for free as a formatted annotation,
    // because this client already sends Prefer: odata.include-annotations="*".
    const select =
      "$select=workflowid,name,category,statecode,statuscode,createdon,modifiedon,clientdata,solutionid,_ownerid_value";
    const orderby = "$orderby=name asc";
    const headers = await this.getAuthHeaders();

    const fetchFlows = async (filter: string): Promise<SolutionFlow[]> => {
      const url = this.buildUrl("workflows", [select, `$filter=${filter}`, orderby].join("&"));
      const response = await this.request(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value: Record<string, unknown>[] }>(response, "List solution flows");
      return data.value.map((row) => {
        const ownerName = row["_ownerid_value@OData.Community.Display.V1.FormattedValue"];
        return {
          ...row,
          // Keep the shape callers already render, so this stays a fix rather
          // than a breaking change.
          ...(typeof ownerName === "string" ? { ownerid: { name: ownerName } } : {}),
        } as unknown as SolutionFlow;
      });
    };

    return wrapApiCall(async () => {
      if (!solutionId) {
        // Modern cloud flows are category 5.
        return fetchFlows("category eq 5");
      }
      // Membership is recorded in `solutioncomponents` (componenttype 29 =
      // workflow). `workflow.solutionid` names the solution the flow was
      // CREATED in, so a flow added to a solution afterwards never matched —
      // a solution whose export carried a Workflows/ folder listed "no flows".
      const safeSolutionId = validateRowId(solutionId);
      const membersUrl = this.buildUrl(
        "solutioncomponents",
        `$select=objectid&$filter=_solutionid_value eq ${safeSolutionId} and componenttype eq 29`,
      );
      const membersResponse = await this.request(membersUrl, { method: "GET", headers });
      const members = await this.handleResponse<{ value: Array<{ objectid?: string }> }>(membersResponse, "List solution components");
      const ids = members.value.map((m) => m.objectid).filter((v): v is string => typeof v === "string" && /^[0-9a-f-]{36}$/i.test(v));
      if (ids.length === 0) return [];
      const flows: SolutionFlow[] = [];
      for (let i = 0; i < ids.length; i += 20) {
        const chunk = ids.slice(i, i + 20).map((id) => `workflowid eq ${id}`).join(" or ");
        flows.push(...(await fetchFlows(`category eq 5 and (${chunk})`)));
      }
      flows.sort((x, y) => String((x as unknown as Record<string, unknown>)["name"] ?? "").localeCompare(String((y as unknown as Record<string, unknown>)["name"] ?? "")));
      return flows;
    }, "List solution flows");
  }

  /**
   * Get a flow by workflow ID
   */
  async getSolutionFlow(workflowId: string): Promise<SolutionFlow> {
    const safeWorkflowId = validateRowId(workflowId);
    logger.debug({ workflowId: safeWorkflowId }, "Getting solution flow");

    const url = this.buildUrl(`workflows(${safeWorkflowId})`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<SolutionFlow>(response, `Get flow ${workflowId}`);
    }, `Get flow ${workflowId}`);
  }

  /**
   * Activate or deactivate a solution flow
   */
  async setSolutionFlowState(workflowId: string, activate: boolean): Promise<void> {
    const safeWorkflowId = validateRowId(workflowId);
    logger.info({ workflowId: safeWorkflowId, activate }, "Setting solution flow state");

    const url = this.buildUrl(`workflows(${safeWorkflowId})`);
    const headers = await this.getAuthHeaders();

    const body = {
      statecode: activate ? 1 : 0,
      statuscode: activate ? 2 : 1,
    };

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
      });
      await this.handleResponse<unknown>(response, `Set flow state ${workflowId}`);
      logger.info({ workflowId, activate }, "Flow state updated");
    }, `Set flow state ${workflowId}`);
  }

  // ==========================================================================
  // Canvas Apps
  // ==========================================================================

  /**
   * List canvas apps
   */
  async listCanvasApps(): Promise<CanvasApp[]> {
    logger.debug("Listing canvas apps");

    // Checked against the live table metadata: `canvasapp` has NO `createdon`
    // or `modifiedon` — it uses `createdtime` and `lastmodifiedtime` — and no
    // `owner` column; the owner is the polymorphic `ownerid`. Asking for any
    // of them fails the WHOLE query, so this listing never returned anything.
    const select =
      "$select=canvasappid,name,displayname,description,appversion,status,createdtime,lastmodifiedtime,_ownerid_value";
    const orderby = "$orderby=displayname asc";
    const query = [select, orderby].join("&");

    const url = this.buildUrl("canvasapps", query);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value: Record<string, unknown>[] }>(
        response,
        "List canvas apps",
      );
      const apps = data.value.map((row) => {
        const ownerName = row["_ownerid_value@OData.Community.Display.V1.FormattedValue"];
        return {
          ...row,
          ...(typeof ownerName === "string" ? { owner: ownerName } : {}),
        } as unknown as CanvasApp;
      });
      logger.info({ count: apps.length }, "Listed canvas apps");
      return apps;
    }, "List canvas apps");
  }

  /**
   * Get a canvas app by ID
   */
  async getCanvasApp(appId: string): Promise<CanvasApp> {
    const safeAppId = validateRowId(appId);
    logger.debug({ appId: safeAppId }, "Getting canvas app");

    const url = this.buildUrl(`canvasapps(${safeAppId})`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<CanvasApp>(response, `Get canvas app ${appId}`);
    }, `Get canvas app ${appId}`);
  }

  // ==========================================================================
  // AI Builder Models
  // ==========================================================================

  /**
   * List AI Builder models
   */
  async listAIModels(): Promise<AIModel[]> {
    logger.debug("Listing AI models");

    const select =
      "$select=msdyn_aimodelid,msdyn_name,msdyn_modelcreationcontext,statecode,statuscode,createdon,modifiedon";
    const orderby = "$orderby=msdyn_name asc";
    const query = [select, orderby].join("&");

    const url = this.buildUrl("msdyn_aimodels", query);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value: AIModel[] }>(response, "List AI models");
      logger.info({ count: data.value.length }, "Listed AI models");
      return data.value;
    }, "List AI models");
  }

  /**
   * Get an AI model by ID
   */
  async getAIModel(modelId: string): Promise<AIModel> {
    const safeModelId = validateRowId(modelId);
    logger.debug({ modelId: safeModelId }, "Getting AI model");

    const url = this.buildUrl(`msdyn_aimodels(${safeModelId})`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<AIModel>(response, `Get AI model ${modelId}`);
    }, `Get AI model ${modelId}`);
  }

  // ==========================================================================
  // Approvals (via Dataverse)
  // ==========================================================================

  /**
   * List pending approvals
   */
  async listApprovalsDataverse(): Promise<ApprovalRequest[]> {
    logger.debug("Listing approvals from Dataverse");

    // Every part of this query used to be wrong, and each error masked the
    // next. Checked against the live table metadata:
    //   - there is NO `..._title` column; the name column is `..._name`
    //   - there is NO `..._requestdate` column; the request time is `createdon`
    //   - `..._stage` is a PICKLIST (192350000 Not Specified, 192350001 Basic,
    //     192351000 Complete), so comparing it to the strings 'Basic'/'Pending'
    //     was invalid — and 'Pending' is not even one of its values
    //   - `ownerid` is a POLYMORPHIC lookup to `principal`, which has no
    //     `name`, so expanding it failed the whole request.
    // The owner's name comes from the `_ownerid_value` formatted annotation.
    // NOT from `owneridname`: that companion column appears in the table
    // METADATA but is not selectable through the Web API, which rejects it
    // with "Could not find a property named 'owneridname'". Metadata listing
    // a column does not mean the Web API will serve it.
    const select =
      "$select=msdyn_flow_approvalid,msdyn_flow_approval_name,msdyn_flow_approval_stage,msdyn_flow_approval_result,createdon,msdyn_flow_approval_dueon,_ownerid_value";
    // "Pending" means not finished: anything that has not reached Complete.
    const filter = `$filter=msdyn_flow_approval_stage ne ${APPROVAL_STAGE_COMPLETE}`;
    const orderby = "$orderby=createdon desc";
    const query = [select, filter, orderby].join("&");

    const url = this.buildUrl("msdyn_flow_approvals", query);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value: Record<string, unknown>[] }>(
        response,
        "List approvals",
      );
      const approvals = data.value.map((row) => {
        const ownerName = row["_ownerid_value@OData.Community.Display.V1.FormattedValue"];
        const stageLabel = row["msdyn_flow_approval_stage@OData.Community.Display.V1.FormattedValue"];
        return {
          ...row,
          // Keep the ownerid.name shape the renderer already uses, so this is
          // a fix rather than a breaking change to callers.
          ...(typeof ownerName === "string" ? { ownerid: { name: ownerName } } : {}),
          ...(typeof stageLabel === "string" ? { msdyn_flow_approval_stage_label: stageLabel } : {}),
        } as unknown as ApprovalRequest;
      });
      logger.info({ count: approvals.length }, "Listed approvals");
      return approvals;
    }, "List approvals");
  }

  // ==========================================================================
  // Table Metadata (EntityDefinitions)
  // ==========================================================================

  /**
   * List all tables (entities) in the environment
   */
  async listTables(options: { filter?: string; top?: number } = {}): Promise<DataverseTable[]> {
    logger.debug(options, "Listing Dataverse tables");

    // EntityDefinitions does not support $top or $orderby — sort/cap client-side.
    // It also rejects contains()/startswith()/endswith() ("The contains function
    // isn't supported for Metadata Entities"), so substring searches — the thing
    // people actually want from a table list — are done here, on the full list.
    const select =
      "$select=LogicalName,DisplayName,EntitySetName,Description,IsCustomEntity,IsManaged,SchemaName,PrimaryIdAttribute,PrimaryNameAttribute";
    const parts = [select];
    const local = options.filter ? parseTableFilter(options.filter) : null;
    if (options.filter && !local) parts.push(`$filter=${validateODataFilter(options.filter)}`);

    const url = this.buildUrl("EntityDefinitions", parts.join("&"));
    const headers = await this.getAuthHeaders();
    const cap = options.top ?? 100;

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value: DataverseTable[] }>(response, "List tables");
      const matched = local ? data.value.filter((t) => tableMatches(t, local)) : data.value;
      const sorted = [...matched].sort((a, b) => a.LogicalName.localeCompare(b.LogicalName));
      const limited = sorted.slice(0, cap);
      logger.info({ count: limited.length, total: data.value.length, local: local !== null }, "Listed Dataverse tables");
      return limited;
    }, "List tables");
  }

  /**
   * Get table metadata including attribute definitions
   */
  async getTableMetadata(
    logicalName: string,
  ): Promise<{ table: DataverseTable; attributes: DataverseAttribute[] }> {
    const safeName = validateLogicalName(logicalName);
    logger.debug({ logicalName: safeName }, "Getting table metadata");

    const headers = await this.getAuthHeaders();

    // Get table definition
    const tableSelect =
      "$select=LogicalName,DisplayName,EntitySetName,Description,IsCustomEntity,IsManaged,SchemaName,PrimaryIdAttribute,PrimaryNameAttribute";
    const tableUrl = this.buildUrl(`EntityDefinitions(LogicalName='${safeName}')`, tableSelect);

    // Get attributes
    const attrSelect =
      "$select=LogicalName,DisplayName,AttributeType,IsCustomAttribute,RequiredLevel,Description,SchemaName";
    const attrUrl = this.buildUrl(
      `EntityDefinitions(LogicalName='${safeName}')/Attributes`,
      `${attrSelect}&$top=250`,
    );

    return wrapApiCall(async () => {
      const [tableResp, attrResp] = await Promise.all([
        requestWithRetry(tableUrl, { method: "GET", headers }),
        requestWithRetry(attrUrl, { method: "GET", headers }),
      ]);

      const table = await this.handleResponse<DataverseTable>(
        tableResp,
        `Get table ${logicalName}`,
      );
      const attrData = await this.handleResponse<{ value: DataverseAttribute[] }>(
        attrResp,
        `Get attributes for ${logicalName}`,
      );

      logger.info({ logicalName, attrCount: attrData.value.length }, "Got table metadata");
      return { table, attributes: attrData.value };
    }, `Get table metadata ${logicalName}`);
  }

  // ==========================================================================
  // Row CRUD
  // ==========================================================================

  /**
   * Query rows from a Dataverse table
   */
  async queryRows(
    entitySetName: string,
    options: {
      select?: string;
      filter?: string;
      orderBy?: string;
      top?: number;
      expand?: string;
    } = {},
  ): Promise<{ value: DataverseRow[]; nextLink?: string }> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    logger.debug({ entitySetName: safeEntitySet, ...options }, "Querying Dataverse rows");

    const parts: string[] = [];
    if (options.select) parts.push(`$select=${validateODataFieldList(options.select, "$select")}`);
    if (options.filter) parts.push(`$filter=${validateODataFilter(options.filter)}`);
    if (options.orderBy)
      parts.push(`$orderby=${validateODataFieldList(options.orderBy, "$orderby")}`);
    if (options.expand) parts.push(`$expand=${validateODataExpand(options.expand)}`);
    parts.push(`$top=${options.top ?? 50}`);

    const url = this.buildUrl(safeEntitySet, parts.join("&"));
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      const data = await this.handleResponse<{ value: DataverseRow[]; "@odata.nextLink"?: string }>(
        response,
        `Query ${safeEntitySet}`,
      );
      logger.info({ entitySetName: safeEntitySet, count: data.value.length }, "Queried rows");
      return { value: data.value, nextLink: data["@odata.nextLink"] };
    }, `Query ${safeEntitySet}`);
  }

  /**
   * Get a single row by ID
   */
  async getRow(entitySetName: string, id: string, select?: string, expand?: string): Promise<DataverseRow> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    const safeId = validateRowId(id);
    logger.debug({ entitySetName: safeEntitySet, id: safeId }, "Getting Dataverse row");

    const parts: string[] = [];
    if (select) parts.push(`$select=${validateODataFieldList(select, "$select")}`);
    if (expand) parts.push(`$expand=${validateODataExpand(expand)}`);
    const query = parts.length > 0 ? parts.join("&") : undefined;
    const url = this.buildUrl(`${safeEntitySet}(${safeId})`, query);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<DataverseRow>(response, `Get row ${id}`);
    }, `Get row ${id}`);
  }

  /** Narrow silent preflight for experimental Activity/Monitor readers. */
  async getCopilotAgentIdentitySilent(
    agentId: string,
    options: { signal: AbortSignal; includeCreatedOn?: boolean },
  ): Promise<{ botid: string; schemaname: string; createdon?: string }> {
    const failure = () =>
      new ApiError(
        "Copilot activity agent identity read unavailable; no fallback or sign-in was attempted.",
        502,
      );
    if (typeof agentId !== "string" || agentId.length !== 36 || !GUID_PATTERN.test(agentId))
      throw failure();
    const org = this.environmentUrl;
    const getSelectedAccount = () => {
      try {
        return this.authProvider.getCurrentAccount();
      } catch {
        throw failure();
      }
    };
    const before = getSelectedAccount();
    if (
      !before ||
      typeof before.username !== "string" ||
      !before.username.trim() ||
      typeof before.tenantId !== "string" ||
      before.tenantId.length !== 36 ||
      !GUID_PATTERN.test(before.tenantId) ||
      typeof before.localAccountId !== "string" ||
      before.localAccountId.length !== 36 ||
      !GUID_PATTERN.test(before.localAccountId)
    )
      throw failure();
    const selected = {
      username: before.username,
      tenantId: before.tenantId,
      objectId: before.localAccountId,
    };
    const controller = new AbortController();
    const abort = () => controller.abort();
    let body: Awaited<ReturnType<typeof requestWithRetry>>["body"] | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const destroy = () => {
      try {
        body?.destroy();
      } catch {
        /* Cleanup must not expose raw errors. */
      }
    };
    const active = () => {
      if (controller.signal.aborted || this.environmentUrl !== org) {
        destroy();
        throw failure();
      }
      const now = getSelectedAccount();
      if (
        !now ||
        now.username !== selected.username ||
        now.tenantId !== selected.tenantId ||
        now.localAccountId !== selected.objectId
      ) {
        destroy();
        throw failure();
      }
    };
    options.signal.addEventListener("abort", abort, { once: true });
    if (options.signal.aborted) controller.abort();
    const work = async () => {
      active();
      const token = await this.authProvider.tryGetTokenForResourceSilent?.(`https://${org}`);
      active();
      if (typeof token !== "string" || !token.trim() || /\s/.test(token)) throw failure();
      const response = await requestWithRetry(
        `https://${org}/api/data/v9.2/bots(${agentId.toLowerCase()})?$select=botid,schemaname${options.includeCreatedOn === true ? ",createdon" : ""}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          signal: controller.signal,
          headersTimeout: 10000,
          bodyTimeout: 10000,
        },
        { maxAttempts: 1 },
      );
      body = response.body;
      active();
      if (
        response.statusCode !== 200 ||
        typeof response.headers["content-type"] !== "string" ||
        !/^application\/(?:json|[a-z0-9.+-]+\+json)(?:\s*;|$)/i.test(
          response.headers["content-type"],
        )
      )
        throw failure();
      const chunks: Buffer[] = [];
      let size = 0;
      for await (const chunk of body as AsyncIterable<Uint8Array>) {
        active();
        const bytes = Buffer.from(chunk);
        size += bytes.length;
        if (size > 1024 * 1024) throw failure();
        chunks.push(bytes);
      }
      active();
      const value: unknown = JSON.parse(Buffer.concat(chunks).toString("utf8"));
      if (!value || typeof value !== "object" || Array.isArray(value)) throw failure();
      const row = value as Record<string, unknown>;
      if (
        typeof row.botid !== "string" ||
        row.botid.length !== 36 ||
        !GUID_PATTERN.test(row.botid) ||
        row.botid.toLowerCase() !== agentId.toLowerCase() ||
        typeof row.schemaname !== "string" ||
        !/^[A-Za-z_][A-Za-z0-9_]{0,199}$(?![\s\S])/.test(row.schemaname)
      )
        throw failure();
      if (options.includeCreatedOn === true &&
          (typeof row.createdon !== "string" || row.createdon.length === 0 || row.createdon.length > 64)) throw failure();
      return { botid: row.botid, schemaname: row.schemaname,
        ...(options.includeCreatedOn === true ? { createdon: row.createdon as string } : {}) };
    };
    try {
      return await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          const rejectAbort = () => {
            destroy();
            reject(failure());
          };
          controller.signal.addEventListener("abort", rejectAbort, { once: true });
          if (controller.signal.aborted) rejectAbort();
          timer = setTimeout(abort, 10000);
        }),
      ]);
    } catch {
      throw failure();
    } finally {
      clearTimeout(timer);
      options.signal.removeEventListener("abort", abort);
      controller.abort();
      destroy();
    }
  }

  /**
   * Create a new row
   */
  async createRow(entitySetName: string, data: Record<string, unknown>): Promise<DataverseRow> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    logger.info({ entitySetName: safeEntitySet }, "Creating Dataverse row");

    const url = this.buildUrl(safeEntitySet);
    const headers = await this.getAuthHeaders();
    headers["Prefer"] = "return=representation";

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
      const row = await this.handleResponse<DataverseRow>(
        response,
        `Create row in ${safeEntitySet}`,
      );
      logger.info({ entitySetName: safeEntitySet }, "Created Dataverse row");
      return row;
    }, `Create row in ${entitySetName}`);
  }

  /**
   * Update an existing row
   */
  async updateRow(entitySetName: string, id: string, data: Record<string, unknown>): Promise<void> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    const safeId = validateRowId(id);
    logger.info({ entitySetName: safeEntitySet, id: safeId }, "Updating Dataverse row");

    const url = this.buildUrl(`${safeEntitySet}(${safeId})`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
      await this.handleResponse<unknown>(response, `Update row ${id}`);
      logger.info({ entitySetName, id }, "Updated Dataverse row");
    }, `Update row ${id}`);
  }

  /**
   * Delete a row
   */
  async deleteRow(entitySetName: string, id: string): Promise<void> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    const safeId = validateRowId(id);
    logger.info({ entitySetName: safeEntitySet, id: safeId }, "Deleting Dataverse row");

    const url = this.buildUrl(`${safeEntitySet}(${safeId})`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "DELETE",
        headers,
      });
      // DELETE returns 204 No Content
      if (response.statusCode >= 400) {
        await this.handleResponse<unknown>(response, `Delete row ${id}`);
      } else {
        // Consume the body
        await response.body.text();
      }
      logger.info({ entitySetName, id }, "Deleted Dataverse row");
    }, `Delete row ${id}`);
  }

  // ==========================================================================
  // Dataverse depth: FetchXML, upsert, assign, search, metadata, schema
  // write, and $batch. Added 2026-08 for the "Dataverse depth" feature set.
  // ==========================================================================

  /**
   * Execute a FetchXML query. FetchXML covers what OData can't (or can't
   * sanely): aggregates, grouping, and multi-hop link-entity joins.
   */
  async executeFetchXml(
    entitySetName: string,
    fetchXml: string,
  ): Promise<{ value: DataverseRow[] }> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    if (fetchXml.length > 100_000) {
      throw new ApiError("FetchXML too large (max 100KB)", 413);
    }
    logger.info({ entitySetName: safeEntitySet }, "Executing FetchXML query");

    const url = this.buildUrl(safeEntitySet, `fetchXml=${encodeURIComponent(fetchXml)}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<{ value: DataverseRow[] }>(response, "FetchXML query");
    }, "FetchXML query");
  }

  /**
   * Create-or-update a row addressed by alternate key(s) — no GUID needed.
   * Returns which outcome occurred (via Prefer: return=representation).
   */
  async upsertRow(
    entitySetName: string,
    keys: Record<string, string>,
    data: Record<string, unknown>,
  ): Promise<{ outcome: "created" | "updated"; row: DataverseRow }> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    const keySegment = Object.entries(keys)
      .map(([name, value]) => {
        const safeName = validateLogicalName(name);
        // Integer-typed alternate keys go unquoted; everything else is a
        // quoted OData string literal with '' escaping.
        const literal = /^-?\d+$/.test(value) ? value : `'${value.replace(/'/g, "''")}'`;
        return `${safeName}=${literal}`;
      })
      .join(",");
    logger.info({ entitySetName: safeEntitySet }, "Upserting Dataverse row by alternate key");

    const url = this.buildUrl(`${safeEntitySet}(${keySegment})`);
    const headers = {
      ...(await this.getAuthHeaders()),
      "Content-Type": "application/json",
      Prefer: "return=representation",
    };

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
      const row = await this.handleResponse<DataverseRow>(response, "Upsert row");
      return { outcome: response.statusCode === 201 ? "created" as const : "updated" as const, row };
    }, "Upsert row");
  }

  /** Reassign a row's owner to a user or team. */
  async assignRow(
    entitySetName: string,
    id: string,
    ownerType: "user" | "team",
    ownerId: string,
  ): Promise<void> {
    const safeEntitySet = validateEntitySetName(entitySetName);
    const safeId = validateRowId(id);
    const safeOwnerId = validateRowId(ownerId);
    const ownerSet = ownerType === "team" ? "teams" : "systemusers";
    logger.info({ entitySetName: safeEntitySet, id: safeId, ownerType }, "Assigning Dataverse row");

    const url = this.buildUrl(`${safeEntitySet}(${safeId})`);
    const headers = { ...(await this.getAuthHeaders()), "Content-Type": "application/json" };

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ "ownerid@odata.bind": `/${ownerSet}(${safeOwnerId})` }),
      });
      if (response.statusCode >= 400) {
        await this.handleResponse<unknown>(response, "Assign row");
      } else {
        await response.body.text();
      }
    }, "Assign row");
  }

  /**
   * Full-text Relevance Search across the tables an admin has enabled for it.
   * Requires the environment's "Dataverse search" setting to be on — a 403/
   * "not enabled" response is an environment limitation, not a bug.
   */
  async searchRows(
    search: string,
    entities?: string[],
    top?: number,
  ): Promise<{
    value: Array<Record<string, unknown>>;
    totalrecordcount?: number;
  }> {
    logger.info({ entities }, "Executing Dataverse relevance search");
    const url = `https://${this.environmentUrl}/api/search/v1.0/query`;
    const headers = { ...(await this.getAuthHeaders()), "Content-Type": "application/json" };

    const body: Record<string, unknown> = { search, top: top ?? 25 };
    if (entities && entities.length > 0) {
      body["entities"] = JSON.stringify(entities.map((e) => validateLogicalName(e)));
    }

    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      return this.handleResponse<{ value: Array<Record<string, unknown>>; totalrecordcount?: number }>(
        response,
        "Relevance search",
      );
    }, "Relevance search");
  }

  /** Option-set (choice) values for a global option set by name. */
  async getGlobalOptionSet(name: string): Promise<Record<string, unknown>> {
    const safeName = validateLogicalName(name);
    const url = this.buildUrl(`GlobalOptionSetDefinitions(Name='${safeName}')`);
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<Record<string, unknown>>(response, `Global option set ${safeName}`);
    }, `Global option set ${safeName}`);
  }

  /**
   * Option-set (choice) values for a table column. The Web API only exposes
   * Options through a type cast, and the column could be any of four choice
   * types — try each until one answers.
   */
  async getAttributeOptionSet(
    tableLogicalName: string,
    columnLogicalName: string,
  ): Promise<{ cast: string; metadata: Record<string, unknown> }> {
    const safeTable = validateLogicalName(tableLogicalName);
    const safeColumn = validateLogicalName(columnLogicalName);
    const headers = await this.getAuthHeaders();

    const casts = [
      "PicklistAttributeMetadata",
      "MultiSelectPicklistAttributeMetadata",
      "StatusAttributeMetadata",
      "StateAttributeMetadata",
      "BooleanAttributeMetadata",
    ];

    return wrapApiCall(async () => {
      let lastError: unknown;
      for (const cast of casts) {
        const url = this.buildUrl(
          `EntityDefinitions(LogicalName='${safeTable}')/Attributes(LogicalName='${safeColumn}')/Microsoft.Dynamics.CRM.${cast}`,
          "$select=LogicalName&$expand=OptionSet,GlobalOptionSet",
        );
        try {
          const response = await this.request(url, { method: "GET", headers });
          if (response.statusCode >= 400) {
            await response.body.text();
            continue;
          }
          const metadata = await this.handleResponse<Record<string, unknown>>(
            response,
            `Option set ${safeTable}.${safeColumn}`,
          );
          return { cast, metadata };
        } catch (err) {
          lastError = err;
        }
      }
      throw lastError instanceof Error
        ? lastError
        : new ApiError(
            `Column '${safeColumn}' on '${safeTable}' is not a choice column (tried Picklist, MultiSelect, Status, State, Boolean).`,
            404,
          );
    }, `Option set ${safeTable}.${safeColumn}`);
  }

  /** All relationships (1:N, N:1, N:N) declared on a table. */
  async getRelationships(logicalName: string): Promise<Record<string, unknown>> {
    const safeName = validateLogicalName(logicalName);
    const url = this.buildUrl(
      `EntityDefinitions(LogicalName='${safeName}')`,
      "$select=LogicalName&$expand=" +
        "OneToManyRelationships($select=SchemaName,ReferencedEntity,ReferencingEntity,ReferencingAttribute,ReferencedEntityNavigationPropertyName)," +
        "ManyToOneRelationships($select=SchemaName,ReferencedEntity,ReferencingEntity,ReferencingAttribute,ReferencingEntityNavigationPropertyName)," +
        "ManyToManyRelationships($select=SchemaName,Entity1LogicalName,Entity2LogicalName,IntersectEntityName)",
    );
    const headers = await this.getAuthHeaders();
    return wrapApiCall(async () => {
      const response = await this.request(url, { method: "GET", headers });
      return this.handleResponse<Record<string, unknown>>(response, `Relationships for ${safeName}`);
    }, `Relationships for ${safeName}`);
  }

  /** Create a column (attribute) on a table. Caller builds the typed metadata. */
  async createColumn(
    tableLogicalName: string,
    attributeMetadata: Record<string, unknown>,
  ): Promise<void> {
    const safeTable = validateLogicalName(tableLogicalName);
    logger.info({ table: safeTable }, "Creating Dataverse column");
    const url = this.buildUrl(`EntityDefinitions(LogicalName='${safeTable}')/Attributes`);
    const headers = { ...(await this.getAuthHeaders()), "Content-Type": "application/json" };
    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "POST",
        headers,
        body: JSON.stringify(attributeMetadata),
      });
      if (response.statusCode >= 400) {
        await this.handleResponse<unknown>(response, "Create column");
      } else {
        await response.body.text();
      }
    }, "Create column");
  }

  /**
   * Create an N:1 lookup column and its relationship in one call — the only
   * supported way to add a foreign-key reference through the Web API.
   */
  async createLookupColumn(relationshipMetadata: Record<string, unknown>): Promise<void> {
    logger.info({}, "Creating Dataverse lookup column + relationship");
    const url = this.buildUrl("RelationshipDefinitions");
    const headers = { ...(await this.getAuthHeaders()), "Content-Type": "application/json" };
    return wrapApiCall(async () => {
      const response = await this.request(url, {
        method: "POST",
        headers,
        body: JSON.stringify(relationshipMetadata),
      });
      if (response.statusCode >= 400) {
        await this.handleResponse<unknown>(response, "Create lookup column");
      } else {
        await response.body.text();
      }
    }, "Create lookup column");
  }

  /**
   * Execute up to 100 operations in one OData $batch request. With
   * `atomic: true` the operations are wrapped in a changeset — all succeed
   * or all roll back (GETs are not allowed in changesets, per OData).
   */
  async executeBatch(
    operations: Array<{
      method: "GET" | "POST" | "PATCH" | "DELETE";
      path: string;
      data?: Record<string, unknown>;
    }>,
    atomic: boolean,
  ): Promise<Array<{ status: number; body: string }>> {
    if (operations.length === 0 || operations.length > 100) {
      throw new ApiError("Batch must contain 1-100 operations", 400);
    }
    logger.info({ count: operations.length, atomic }, "Executing Dataverse $batch");

    const batchId = `batch_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    const changesetId = `changeset_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    const apiBase = `https://${this.environmentUrl}/api/data/${this.apiVersion}`;

    const renderOp = (
      op: (typeof operations)[number],
      contentId?: number,
    ): string => {
      const lines = [
        "Content-Type: application/http",
        "Content-Transfer-Encoding: binary",
      ];
      if (contentId !== undefined) lines.push(`Content-ID: ${contentId}`);
      lines.push("", `${op.method} ${apiBase}/${op.path} HTTP/1.1`);
      if (op.data !== undefined) {
        lines.push("Content-Type: application/json;type=entry", "", JSON.stringify(op.data));
      } else {
        lines.push("");
      }
      return lines.join("\r\n");
    };

    let bodyText: string;
    if (atomic) {
      const parts = operations.map((op, i) => `--${changesetId}\r\n${renderOp(op, i + 1)}`);
      bodyText =
        `--${batchId}\r\n` +
        `Content-Type: multipart/mixed;boundary=${changesetId}\r\n\r\n` +
        `${parts.join("\r\n")}\r\n--${changesetId}--\r\n--${batchId}--\r\n`;
    } else {
      const parts = operations.map((op) => `--${batchId}\r\n${renderOp(op)}`);
      bodyText = `${parts.join("\r\n")}\r\n--${batchId}--\r\n`;
    }

    const headers = {
      ...(await this.getAuthHeaders()),
      "Content-Type": `multipart/mixed;boundary=${batchId}`,
    };

    return wrapApiCall(async () => {
      const response = await this.request(this.buildUrl("$batch"), {
        method: "POST",
        headers,
        body: bodyText,
      });
      const { statusCode } = response;
      const text = await response.body.text();
      if (statusCode >= 400) {
        throw new ApiError("Batch request failed", statusCode, sanitizeErrorMessage(text.slice(0, 2000)));
      }
      // Parse per-operation results out of the multipart response: each part
      // carries an "HTTP/1.1 <status>" line followed by headers and a body.
      const results: Array<{ status: number; body: string }> = [];
      const statusRe = /HTTP\/1\.1 (\d{3})[^\r\n]*\r?\n((?:[^\r\n]+\r?\n)*)\r?\n?([^]*?)(?=(?:--(?:batch|changeset|b_|cs_)[A-Za-z0-9_-]*)|$)/g;
      let match: RegExpExecArray | null;
      while ((match = statusRe.exec(text)) !== null) {
        const status = Number.parseInt(match[1]!, 10);
        const body = (match[3] ?? "").trim();
        results.push({ status, body: sanitizeErrorMessage(body.slice(0, 2000)) });
      }
      return results;
    }, "Batch execute");
  }

  // ==========================================================================
  // Environment Discovery
  // ==========================================================================

  /**
   * Construct a best-guess Dataverse hostname from an environment GUID + region.
   *
   * WARNING: this is a last-resort fallback. The real Dataverse hostname is the
   * org's `domainName` (exposed as `linkedEnvironmentMetadata.instanceUrl` on the
   * BAP admin API) which usually does NOT match the environment GUID. Prefer
   * resolving instanceUrl from BAP, or set `dataverseUrl` in the env config.
   */
  static getDataverseUrl(environmentId: string, region = "crm"): string {
    // Common region mappings
    const regionMap: Record<string, string> = {
      // Power Platform region names
      unitedstates: "crm",
      europe: "crm4",
      uk: "crm11",
      asia: "crm5",
      australia: "crm6",
      japan: "crm7",
      india: "crm8",
      canada: "crm3",
      southamerica: "crm2",
      france: "crm12",
      uae: "crm15",
      switzerland: "crm17",
      germany: "crm16",
      government: "crm9",
      gcc: "crm9",
      gcchigh: "crm.microsoft.us",
      dod: "crm.appsplatform.us",
      // Azure region names (map to same CRM domains)
      westus: "crm",
      eastus: "crm",
      centralus: "crm",
      westus2: "crm",
      eastus2: "crm",
      southcentralus: "crm",
      northcentralus: "crm",
      westeurope: "crm4",
      northeurope: "crm4",
      uksouth: "crm11",
      ukwest: "crm11",
      eastasia: "crm5",
      southeastasia: "crm5",
      australiaeast: "crm6",
      australiasoutheast: "crm6",
      japaneast: "crm7",
      japanwest: "crm7",
      centralindia: "crm8",
      southindia: "crm8",
      canadacentral: "crm3",
      canadaeast: "crm3",
      brazilsouth: "crm2",
      francecentral: "crm12",
      francesouth: "crm12",
      switzerlandnorth: "crm17",
      germanywestcentral: "crm16",
      uaenorth: "crm15",
    };

    const crmDomain = regionMap[region.toLowerCase()] || region;
    return `${environmentId}.${crmDomain}.dynamics.com`;
  }
}

/** A table-list filter evaluated client-side: a bare keyword, or contains/startswith/endswith on a name field. */
export interface LocalTableFilter {
  mode: "contains" | "startswith" | "endswith";
  field: "LogicalName" | "SchemaName" | "EntitySetName" | "DisplayName" | "any";
  needle: string;
}

/**
 * Decide whether a table filter must be evaluated locally. Returns null for a
 * real OData expression (e.g. `IsCustomEntity eq true`, `LogicalName eq 'account'`),
 * which the metadata endpoint does support.
 */
export function parseTableFilter(filter: string): LocalTableFilter | null {
  const trimmed = filter.trim();
  const fn = /^(contains|startswith|endswith)\(\s*(LogicalName|SchemaName|EntitySetName|DisplayName)\s*,\s*'([^']*)'\s*\)$/i.exec(trimmed);
  if (fn) {
    return {
      mode: fn[1]!.toLowerCase() as LocalTableFilter["mode"],
      field: fn[2] as LocalTableFilter["field"],
      needle: fn[3]!.toLowerCase(),
    };
  }
  // A bare word with no OData operator is a keyword search across the name fields.
  if (/^[\w.-]+$/.test(trimmed) && !/\b(eq|ne|gt|ge|lt|le|and|or|not)\b/i.test(trimmed)) {
    return { mode: "contains", field: "any", needle: trimmed.toLowerCase() };
  }
  return null;
}

export function tableMatches(table: DataverseTable, filter: LocalTableFilter): boolean {
  const fields: string[] =
    filter.field === "any"
      ? [table.LogicalName, table.SchemaName, table.EntitySetName, table.DisplayName?.UserLocalizedLabel?.Label ?? ""]
      : filter.field === "DisplayName"
        ? [table.DisplayName?.UserLocalizedLabel?.Label ?? ""]
        : [table[filter.field] ?? ""];
  return fields.some((raw) => {
    const value = String(raw ?? "").toLowerCase();
    if (filter.mode === "startswith") return value.startsWith(filter.needle);
    if (filter.mode === "endswith") return value.endsWith(filter.needle);
    return value.includes(filter.needle);
  });
}
